import {
  Area,
  AreaTranslation,
  DropdownOption,
  EngineMode,
  Period,
  Periods,
} from "../types";
import { AreaService } from "./area-service";
import { moduleSettings } from "../stores/module-settings.store";
import calendarService from "./calendar-service";

class FiltersService {
  static service: FiltersService | undefined;

  static get(): FiltersService {
    if (!this.service) {
      this.service = new FiltersService();
    }
    return this.service!;
  }

  public areaDropdownOptions(
    areas: Array<Area>,
    selectedArea: string | null
  ): Array<DropdownOption> | null {
    const areasTranslations: Array<{
      id: number;
      translation: AreaTranslation;
    }> = this.loadAreaTranslations(areas);
    const options = this.loadAreaDropdownOption(
      areasTranslations,
      selectedArea
    );
    if (!options.length) {
      return null;
    }
    return options;
  }

  private loadAreaDropdownOption(
    areasTranslations: Array<{ id: number; translation: AreaTranslation }>,
    selectedArea: string | null
  ): Array<DropdownOption> {
    return areasTranslations.map(
      (translation: { id: number; translation: AreaTranslation }) => {
        const displayText = translation.translation.name;
        const value = translation.id;
        return {
          value: value.toString(),
          displayText,
          isSelected: value.toString() === selectedArea?.toString(),
        };
      }
    );
  }

  private loadAreaTranslations(
    areas: Array<Area>
  ): Array<{ id: number; translation: AreaTranslation }> {
    const areasTranslations: Array<{
      id: number;
      translation: AreaTranslation;
    }> = [];
    areas.forEach((area: Area) => {
      let displayedLanguageArea = new AreaService(area).getTranslation();
      if (displayedLanguageArea) {
        areasTranslations.push({
          id: area.id,
          translation: displayedLanguageArea,
        });
      }
    });
    return areasTranslations;
  }

  public getFiltersFromPeriods(
    moduleSettingAreas: Area[],
    timePeriods: Array<Period>,
    engineMode?: string
  ) {
    var areas: Array<any> = [];
    const periods: Array<Periods> = [];
    const canBuildArea = engineMode && engineMode === EngineMode.table;
    timePeriods
      .filter((x) => x != null)
      .forEach((timePeriod: Period) => {
        if (canBuildArea) {
          const areaId = timePeriod.area_id;
          if (areaId && !areas.map((area: Area) => area.id).includes(areaId)) {
            const area = moduleSettingAreas.find(
              (area: Area) => area.id === areaId
            );
            if (area) {
              areas.push(area);
            }
          }
        }
        const period = timePeriod.period;
        if (!periods.includes(period)) {
          periods.push(period);
        }
      });
    if (areas.length === 0 && canBuildArea) {
      areas = moduleSettingAreas;
    }
    return {
      areas,
      periods,
    };
  }

  public isAreaSelectionDisabled(
    date: moment.Moment | null,
    periodsState: {
      loading: boolean;
      error: boolean;
    } = { loading: false, error: false }
  ): boolean {
    const forceAreaSelection = moduleSettings.forceAreaSelection;

    // If no forceAreaSelection or no date, don't disable
    if (!forceAreaSelection || date === null) {
      return false;
    }

    // If API error or loading, disable selection
    if (periodsState.error || periodsState.loading) {
      return true;
    }

    // In capacity mode, allow interaction to enable deselection
    // but we will display a warning message
    return false;
  }

  public shouldShowAreaCapacityWarning(date: moment.Moment | null): boolean {
    const forceAreaSelection = moduleSettings.forceAreaSelection;

    if (!forceAreaSelection || date === null) {
      return false;
    }

    const engineMode = calendarService.getEngineMode(date);

    // Show warning if restaurant is in capacity mode
    // AND forceAreaSelection = true (incompatible)
    return forceAreaSelection && engineMode === EngineMode.capacity;
  }
}

export default FiltersService.get();
